import React from 'react';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Search, X as XIcon, MessageSquare } from 'lucide-react';

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const ConversationItem = ({ conv, active, onClick }) => {
  const watchCount = conv.watches?.length || 0;
  const hasPurchased = conv.watches?.some(w => w.status === 'purchased');
  return (
    <button onClick={onClick} data-testid={`conv-${conv.id}`}
      className={`w-full text-left px-3 py-3 border-b border-border/40 transition-colors ${active ? 'bg-primary/8 border-l-2 border-l-primary' : 'hover:bg-muted/30'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm truncate">{conv.user_name}</span>
            {conv.unread_admin > 0 && (
              <Badge className="bg-primary text-primary-foreground text-[10px] h-4 min-w-[16px] px-1 justify-center">{conv.unread_admin}</Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground truncate mt-0.5">{conv.last_message}</p>
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            {watchCount > 0 && <span className="text-[9px] text-muted-foreground">{watchCount} watch{watchCount > 1 ? 'es' : ''}</span>}
            {hasPurchased && <Badge variant="outline" className="text-[8px] h-3.5 px-1 bg-green-500/15 text-green-400 border-green-500/30">Paid</Badge>}
          </div>
        </div>
        <span className="text-[10px] text-muted-foreground shrink-0 mt-0.5">{formatTime(conv.last_message_at)}</span>
      </div>
    </button>
  );
};

const ConversationSidebar = ({ conversations, activeConvId, onSelect, search, onSearchChange, unreadTotal }) => (
  <div className="flex flex-col h-full">
    <div className="p-3 border-b border-border bg-muted/20 space-y-2">
      <h2 className="text-sm font-semibold flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-primary" /> Conversations
        {unreadTotal > 0 && <Badge className="bg-primary text-primary-foreground text-[10px] h-4 px-1.5">{unreadTotal}</Badge>}
      </h2>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input value={search} onChange={e => onSearchChange(e.target.value)} placeholder="Search by name or email..." className="h-8 pl-8 pr-8 text-xs" data-testid="conversation-search" />
        {search && <button onClick={() => onSearchChange('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><XIcon className="h-3.5 w-3.5" /></button>}
      </div>
    </div>
    <div className="flex-1 overflow-y-auto" data-testid="conversation-list">
      {(() => {
        const q = search.toLowerCase().trim();
        const filtered = q ? conversations.filter(c => c.user_name.toLowerCase().includes(q) || c.user_email?.toLowerCase().includes(q)) : conversations;
        return filtered.length === 0
          ? <div className="flex flex-col items-center justify-center h-full text-muted-foreground text-sm gap-2 p-4"><MessageSquare className="h-8 w-8 opacity-30" /><span>{q ? 'No match' : 'No conversations'}</span></div>
          : filtered.map(c => <ConversationItem key={c.id} conv={c} active={c.id === activeConvId || c.conversations?.some(cc => cc.id === activeConvId)} onClick={() => onSelect(c.id)} />);
      })()}
    </div>
  </div>
);

export default ConversationSidebar;
